import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  MessageCircle, Phone, Mail, MapPin, Clock,
  ChevronUp, ChevronDown, ArrowRight, Send, 
} from "lucide-react";


import { FaFacebook } from "react-icons/fa";
import { FaInstagram } from "react-icons/fa";
import { FaLinkedin } from "react-icons/fa";
import { FaYoutube } from "react-icons/fa";

import "./Footer.css";


import logo from "../assets/images/BESTMARK DESIGNERS 13.png";

/* ─── useInView ─────────────────────────────────────────────── */
function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}

/* ─── Data ───────────────────────────────────────────────────── */

// Each entry has a label and the React Router path it navigates to
const SERVICE_LINKS = [
  { label: "3D Signage",              path: "/services/3d-signage"    },
  { label: "Branding & Identity",     path: "/services/branding"      },
  { label: "Printing Services",       path: "/services/printing"      },
  { label: "Book Printing",           path: "/services/book-printing" },
  { label: "Packaging Solutions",     path: "/services/packaging"     },
  { label: "Web Design & Dev",        path: "/services/web-design"    },
  { label: "Promotional Merchandise", path: "/services/merchandise"   },
  { label: "Large Format Printing",   path: "/services/large-format"  },
];

const COMPANY_LINKS = [
  { label: "About Us",      path: "/about"     },
  { label: "Portfolio",     path: "/portfolio" },
  { label: "Services",      path: "/services"  },
  { label: "Testimonials",  path: "/about"     },
  { label: "FAQs",          path: "/contact"   },
  { label: "Contact Us",    path: "/contact"   },
];

const RESOURCE_LINKS = [
  { label: "Get a Quote",         path: "/contact"  },
  { label: "Design Guidelines",   path: "/services" },
  { label: "Printing Tips",       path: "/services/printing"      },
  { label: "Book Printing Guide", path: "/services/book-printing" },
  { label: "Branding Insights",   path: "/services/branding"      },
  { label: "Support Center",      path: "/contact"  },
];

const SOCIALS = [
  { Icon: FaFacebook,      label: "Facebook",  color: "#1877F2", hoverBg: "rgba(24,119,242,.2)",  hoverBorder: "rgba(24,119,242,.5)",  href: "#"                              },
  { Icon: FaInstagram,     label: "Instagram", color: "#E1306C", hoverBg: "rgba(225,48,108,.2)",  hoverBorder: "rgba(225,48,108,.5)",  href: "#"                              },
  { Icon: FaLinkedin,      label: "LinkedIn",  color: "#0A66C2", hoverBg: "rgba(10,102,194,.2)",  hoverBorder: "rgba(10,102,194,.5)",  href: "#"                              },
  { Icon: FaYoutube,       label: "TikTok",    color: "#FF0050", hoverBg: "rgba(255,0,80,.2)",    hoverBorder: "rgba(255,0,80,.5)",    href: "#"                              },
  { Icon: MessageCircle, label: "WhatsApp",  color: "#25D366", hoverBg: "rgba(37,211,102,.2)",  hoverBorder: "rgba(37,211,102,.5)", href: "https://wa.me/254794437498"     },
];



/* ─── Shared UI ─────────────────────────────────────────────── */
function Pill({ children }) {
  return (
    <div className="ft-pill">
      <span className="ft-dot" />
      {children}
    </div>
  );
}

/* ─── Social button ──────────────────────────────────────────── */
function SocBtn({ Icon, label, color, href, hoverBg, hoverBorder }) {
  const [hov, setHov] = useState(false);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="ft-soc"
      aria-label={`Follow on ${label}`}
      style={{
        color:       hov ? color       : "rgba(255,255,255,.5)",
        background:  hov ? hoverBg     : "rgba(255,255,255,.06)",
        borderColor: hov ? hoverBorder : "rgba(255,255,255,.1)",
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      <Icon size={16} />
    </a>
  );
}

/* ─── Footer nav link (client-side navigation) ───────────────── */
function FtLink({ label, path }) {
  const navigate = useNavigate();
  return (
    <button
      className="ft-link"
      onClick={() => { navigate(path); window.scrollTo({ top: 0, behavior: "smooth" }); }}
    >
      {label}
    </button>
  );
}

/* ─── Mobile accordion column ────────────────────────────────── */
function AccordionCol({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ft-acc">
      <button
        className="ft-acc__btn"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        {title}
        <ChevronDown
          className={`ft-acc__icon${open ? " open" : ""}`}
          size={16}
        />
      </button>
      <div
        className={`ft-acc__body${open ? " open" : " closed"}`}
        style={{ maxHeight: open ? "600px" : "0" }}
      >
        <div className="ft-acc__inner">{children}</div>
      </div>
    </div>
  );
}

/* ─── CTA SECTION ────────────────────────────────────────────── */
function FooterCTA() {
  const [ref, vis] = useInView(0.1);
  const navigate   = useNavigate();
  return (
    <section className="ft-cta" ref={ref} aria-label="Call to action">
      <div className="ft-cta__grid"  aria-hidden="true" />
      <div className="ft-cta__orb ft-cta__orb--a" aria-hidden="true" />
      <div className="ft-cta__orb ft-cta__orb--b" aria-hidden="true" />
      <div className="ft-cta__sq"   aria-hidden="true" />
      <div className="ft-cta__dot"  aria-hidden="true" />
      <div className="ft-cta__sm"   aria-hidden="true" />

      <div className="ft-cta__inner">
        <div className={`ft-fade${vis ? " in" : ""}`}>
          <Pill>Let's Get Started</Pill>
        </div>

        <h2 className={`ft-cta__h2 ft-fade d1${vis ? " in" : ""}`}>
          Ready To Transform<br />
          <em className="ft-italic">Your Brand?</em>
        </h2>

        <p className={`ft-cta__sub ft-fade d2${vis ? " in" : ""}`}>
          Let's create powerful branding, signage, printing, packaging, book printing,
          and web experiences that help your business grow.
        </p>

        <div className={`ft-cta__btns ft-fade d3${vis ? " in" : ""}`}>
          <button className="ft-btn-r ft-btn--lg" onClick={() => navigate("/contact")}>
            Get Free Quote <ArrowRight size={16} />
          </button>
          <a href="tel:254794437498" className="ft-btn-g ft-btn--lg">
            <Phone size={15} /> Call Us
          </a>
        </div>

        <div className={`ft-cta__trust ft-fade d4${vis ? " in" : ""}`}>
          {["100+ Projects Completed","98% Client Satisfaction","24h Response Time","Nationwide Delivery"].map(b => (
            <div key={b} className="ft-trust-item">
              <div className="ft-trust-check">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#FF2B2B" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              {b}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── BRAND COLUMN ───────────────────────────────────────────── */
function BrandCol() {
  return (
    <>
      {/* Logo image */}
      <div className="ft-brand__logo">
        <img src={logo} alt="Bestmark Designers" className="ft-brand__img" />
      </div>

      <div className="ft-brand__tagline">Design. Print. Brand. Grow.</div>

      

      <div className="ft-brand__socials">
        {SOCIALS.map(s => <SocBtn key={s.label} {...s} />)}
      </div>
    </>
  );
}

/* ─── CONTACT COLUMN ─────────────────────────────────────────── */
function ContactCol() {
  return (
    <>
      <div className="ft-col-head">Contact Information</div>

      <div className="ft-contact-list">
        <a href="tel:254794437498" className="ft-contact-item">
          <div className="ft-contact-icon"><Phone size={14} color="#FF2B2B" /></div>
          +254 794 437 498
        </a>
        {["info@bestmarkdesigner.com","bestmarkdesigner@gmail.com"].map(em => (
          <a key={em} href={`mailto:${em}`} className="ft-contact-item ft-contact-item--wrap">
            <div className="ft-contact-icon" style={{ marginTop: 2 }}><Mail size={14} color="#FF2B2B" /></div>
            {em}
          </a>
        ))}
        <div className="ft-contact-item">
          <div className="ft-contact-icon"><MapPin size={14} color="#FF2B2B" /></div>
          Nairobi, Kenya
        </div>
      </div>

      
    </>
  );
}

/* ─── MAIN FOOTER ────────────────────────────────────────────── */
function MainFooter() {
  const [ref, vis]      = useInView(0.06);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const linkCol = (links) => (
    <div className="ft-link-col">
      {links.map(l => <FtLink key={l.label} label={l.label} path={l.path} />)}
    </div>
  );

  return (
    <footer ref={ref} className="ft-main" role="contentinfo">
      <div className="ft-main__wrap">
        {isMobile ? (
          <>
            <div className={`ft-fade${vis ? " in" : ""}`} style={{ marginBottom: 28, paddingBottom: 24, borderBottom: "1px solid rgba(255,255,255,.07)" }}>
              <BrandCol />
            </div>
            <AccordionCol title="Services">  {linkCol(SERVICE_LINKS)}  </AccordionCol>
            <AccordionCol title="Company">   {linkCol(COMPANY_LINKS)}  </AccordionCol>
            <AccordionCol title="Resources"> {linkCol(RESOURCE_LINKS)} </AccordionCol>
            <AccordionCol title="Contact">   <ContactCol />             </AccordionCol>
          </>
        ) : (
          <div className="ft-cols">
            <div className={`ft-fade${vis ? " in" : ""}`}>          <BrandCol />  </div>
            <div className={`ft-fade d1${vis ? " in" : ""}`}>
              <div className="ft-col-head">Services</div>
              {linkCol(SERVICE_LINKS)}
            </div>
            <div className={`ft-fade d2${vis ? " in" : ""}`}>
              <div className="ft-col-head">Company</div>
              {linkCol(COMPANY_LINKS)}
            </div>
            <div className={`ft-fade d3${vis ? " in" : ""}`}>
              <div className="ft-col-head">Resources</div>
              {linkCol(RESOURCE_LINKS)}
            </div>
            <div className={`ft-fade d4${vis ? " in" : ""}`}><ContactCol /></div>
          </div>
        )}
      </div>

      <div className="ft-main__divider-wrap">
        <div className="ft-gd" />
      </div>

      <div className="ft-bottom">
        <span className="ft-bottom__copy">© 2026 Bestmark Designers. All Rights Reserved.</span>
        <div className="ft-bottom__links">
          {["Privacy Policy","Terms & Conditions","Cookie Policy"].map(l => (
            <button key={l} className="ft-bottom__link">{l}</button>
          ))}
        </div>
        <span className="ft-bottom__credit">
          Designed & Developed by <span className="ft-bottom__brand">Bestmark Designers</span>
        </span>
      </div>
    </footer>
  );
}

/* ─── BACK TO TOP ────────────────────────────────────────────── */
function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <button
      className={`ft-btt${show ? "" : " ft-btt--hidden"}`}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
    >
      <ChevronUp size={20} />
    </button>
  );
}

/* ─── WHATSAPP FAB ───────────────────────────────────────────── */
function WAFab() {
  return (
    <a
      href="https://wa.me/254794437498"
      target="_blank"
      rel="noopener noreferrer"
      className="ft-wa"
      aria-label="Chat on WhatsApp"
    >
      <div className="ft-wa__tooltip">Chat with us!</div>
      <MessageCircle size={24} />
    </a>
  );
}

/* ─── ROOT ───────────────────────────────────────────────────── */
export default function Footer() {
  return (
    <>
      <FooterCTA />
      <div className="ft-gd" />
      <MainFooter />
      <BackToTop />
      <WAFab />
    </>
  );
}